import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiRequestError, apiRequest } from '../lib/api-client';
import { t } from '../lib/i18n';
import { AuthLayout } from './AuthLayout';
import { CheckIcon } from './icons';

/**
 * Where the confirmation e-mail lands (Phase 128).
 *
 * The link has always pointed here — `verificationUrl` builds `/verificar-email?token=…` and has
 * since the first version — and this page did not exist. The router matched nothing, the SPA
 * shell rendered, and the token was never spent: **every account in production stayed
 * `PENDING_VERIFICATION` no matter how many times somebody clicked the link.** Found on the first
 * real sign-up, which is the only place it could have been found: no test asks a route to exist
 * because a server-side template names it.
 *
 * It verifies on arrival rather than behind a button. There is nothing to decide — the person
 * already decided when they clicked the link in their inbox — and a page that asks "confirm?" after
 * an e-mail that asked the same thing is a second door in front of the same room.
 *
 * Verifying twice is not an error anybody needs to see: the second click of a link somebody
 * forwarded to themselves lands on a page that says the address is confirmed, which is true.
 */

const MESSAGES: Record<string, string> = {
  'auth.invalid_token': 'verifyEmail.invalid',
  'auth.token_expired': 'verifyEmail.expired',
  'auth.token_already_used': 'verifyEmail.alreadyDone',
};

type State = 'WORKING' | 'DONE' | 'ALREADY' | 'FAILED';

export function VerifyEmailPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<State>(token ? 'WORKING' : 'FAILED');
  const [error, setError] = useState<string | null>(token ? null : t('verifyEmail.missingToken'));

  /*
   * Once, whatever React does.
   *
   * Strict mode runs effects twice in development, and the second run would spend a token the
   * first had already consumed — turning a successful verification into "this link was already
   * used" on the very screen that just used it.
   */
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;

    started.current = true;

    void (async () => {
      try {
        await apiRequest('/v1/auth/email/verify', { method: 'POST', body: { token } });
        setState('DONE');
      } catch (caught) {
        const code = caught instanceof ApiRequestError ? caught.code : undefined;

        if (code === 'auth.token_already_used') {
          setState('ALREADY');
          return;
        }

        setState('FAILED');
        setError(t(MESSAGES[code ?? ''] ?? 'error.generic'));
      }
    })();
  }, [token]);

  if (state === 'WORKING') {
    return (
      <AuthLayout>
        <div className="auth__done">
          <h1>{t('verifyEmail.title')}</h1>
          <p>{t('verifyEmail.working')}</p>
        </div>
      </AuthLayout>
    );
  }

  if (state === 'DONE' || state === 'ALREADY') {
    return (
      <AuthLayout>
        <div className="auth__done">
          <span className="auth__done-mark" aria-hidden="true">
            <CheckIcon />
          </span>

          <h1>{t('verifyEmail.doneTitle')}</h1>
          <p>{t(state === 'ALREADY' ? 'verifyEmail.alreadyDone' : 'verifyEmail.doneBody')}</p>

          <Link className="button button--primary" to="/entrar">
            {t('verifyEmail.goToLogin')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="auth__done">
        <h1>{t('verifyEmail.title')}</h1>
        <p>{error}</p>

        {/*
          * Signing in is the way to another link: the resend endpoint needs a session, because an
          * endpoint that mails anybody on request is an endpoint that mails anybody on request.
          */}
        <Link className="button button--primary" to="/entrar">
          {t('verifyEmail.goToLogin')}
        </Link>
      </div>
    </AuthLayout>
  );
}
